import type { JSX } from 'react';
import { allNpcs, findTerritory } from '@bratva/content';
import type { SaveDoc } from '@bratva/state';
import { questViews } from '../game/quests.js';

interface Props {
  save: SaveDoc;
  onClose(): void;
}

/** Дела района: что висит на игроке и с кем он в каких отношениях. */
export function QuestPanel({ save, onClose }: Props): JSX.Element {
  const views = questViews(save);
  const active = views.filter((view) => !view.done);
  const done = views.filter((view) => view.done);
  const known = allNpcs().filter((npc) => save.npc[npc.id] !== undefined);

  return (
    <div className="shop">
      <div className="deeds-head">
        <b>Дела и люди</b>
        <button type="button" className="deeds-close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      {active.length === 0 && <p className="shop-item-meta">Пока ничего не висит. Найди, чем заняться.</p>}

      <div className="shop-list">
        {active.map(({ quest, step }) => (
          <div key={quest.id} className="shop-item">
            <div className="shop-item-top">
              <span className="shop-item-title">{quest.title}</span>
              <span className="shop-item-meta">
                шаг {step + 1} / {quest.steps.length}
              </span>
            </div>
            <span className="shop-item-hint">{quest.hint}</span>
            <ol className="quest-steps">
              {quest.steps.map((questStep, index) => (
                <li key={questStep.id} className={index < step ? 'done' : index === step ? 'now' : ''}>
                  {questStep.text}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      {save.territories.length > 0 && (
        <div className="shop-sets">
          <span className="hud-caption">Держишь</span>
          {save.territories.map((held) => {
            const territory = findTerritory(held.id);
            if (!territory) return null;
            const daysHeld = save.day - held.heldSince;
            const safe = save.player.avtoritet >= territory.holdAvtoritet;
            return (
              <p key={held.id} className={safe ? 'set on' : 'set'}>
                <b>{territory.title}</b> <span>+{territory.incomeBonus}% с точек</span>
                <br />
                <span className="shop-item-meta">
                  {daysHeld} дн. · держать от {territory.holdAvtoritet} авторитета
                  {safe ? '' : ' · авторитета не хватает, отожмут'}
                </span>
              </p>
            );
          })}
        </div>
      )}

      {save.brigade.length > 0 && (
        <div className="shop-sets">
          <span className="hud-caption">Бригада</span>
          {save.brigade.map((slot) => (
            <p key={slot.member} className="set on">
              <b>{slot.title}</b> <span>сила {slot.sila} · доля {slot.share}%</span>
            </p>
          ))}
        </div>
      )}

      {known.length > 0 && (
        <div className="shop-sets">
          <span className="hud-caption">Люди района</span>
          {known.map((npc) => {
            const rep = save.npc[npc.id] ?? 0;
            return (
              <p key={npc.id} className={rep < 0 ? 'set' : 'set on'}>
                <b>{npc.title}</b> <span>{rep > 0 ? `+${rep}` : rep}</span>
                <br />
                <span className="shop-item-meta">{npc.gives}</span>
              </p>
            );
          })}
        </div>
      )}

      {done.length > 0 && (
        <div className="shop-sets">
          <span className="hud-caption">Закрыто</span>
          {done.map(({ quest }) => (
            <p key={quest.id} className="set on">
              {quest.title}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
